/** Loads instance branding and applies it as theme + product-name context. */

import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { getBranding } from "@pbxmgr/api";
import type { Branding } from "@pbxmgr/api";
import { Spinner, ThemeProvider } from "@pbxmgr/ui";

const DEFAULT_BRANDING: Branding = {
  product_name: "PBX Manager",
  theme: null,
  login_text: null,
  logo_path: null,
  locale: "en",
};

const BrandingContext = createContext<Branding>(DEFAULT_BRANDING);

/** The instance branding (falls back to defaults if the fetch failed). */
export function useBranding(): Branding {
  return useContext(BrandingContext);
}

/** Fetches branding once, applies the theme, and provides it to descendants. */
export function BrandingProvider({ children }: { children: ReactNode }): ReactNode {
  const { data, isLoading } = useQuery({
    queryKey: ["branding"],
    queryFn: getBranding,
    staleTime: Infinity,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="ops-center">
        <Spinner />
      </div>
    );
  }

  const branding = data ?? DEFAULT_BRANDING;
  return (
    <BrandingContext.Provider value={branding}>
      <ThemeProvider theme={branding.theme}>{children}</ThemeProvider>
    </BrandingContext.Provider>
  );
}
